import type { Schema } from 'yup'
import { object, string, number, boolean } from 'yup'
import type { Resource } from '@/prisma/client'

type SchemaKeys = 'name' | 'quantity' | 'min' | 'max' | 'isExclusive'

export function resourceSchema(fields: SchemaKeys[], t: any) {
  const baseSchema = {
    name: string().required(t('errors.required')).min(2, t('errors.min', { min: 2 })),
    quantity: number()
      .required(t('errors.required'))
      .min(1, t('errors.minValue', { min: 1 }))
      .integer(t('errors.integer')),
    min: number()
      .required(t('errors.required'))
      .min(1, t('errors.minValue', { min: 1 }))
      .integer(t('errors.integer')),
    max: number()
      .nullable()
      .min(1, t('errors.minValue', { min: 1 }))
      .integer(t('errors.integer'))
      .test('max-greater-than-min', t('errors.maxGreaterThanMin'), function (value) {
        return !value || value >= this.parent.min
      }),
    isExclusive: boolean().default(true),
  }

  const partialSchema: Partial<Record<SchemaKeys, Schema<any>>> = {}
  fields.forEach((fieldName) => {
    if (baseSchema[fieldName]) {
      partialSchema[fieldName] = baseSchema[fieldName]
    }
  })
  return object(partialSchema)
}

export const useResourceStore = defineStore('resource', () => {
  const resources = ref<Resource[]>([])

  const normalizeData = (resource: any) => {
    resource.activity = {
      connect: { id: resource.activityId },
    }
    return resource
  }

  async function fetch(activityId: number) {
    try {
      resources.value = await $fetch<any | null>(`/api/resource?activityId=${activityId}`)

      return { data: resources.value, status: 'success', error: null }
    } catch (e: any) {
      return { data: resources.value, status: 'error', error: e.data || e }
    }
  }

  async function upsert(resource: Partial<Resource>, defaultSlots = false) {
    try {
      const result = await $fetch<Resource | null>('/api/resource', {
        method: 'POST',
        body: normalizeData(resource),
        query: { defaultSlots: defaultSlots },
      })

      if (result) {
        const index = resources.value?.findIndex(r => r.id === result.id)
        if (index !== undefined && index > -1) {
          resources.value?.splice(index, 1, result)
        } else {
          resources.value = [...(resources.value || []), result]
        }
      }
      return { data: result, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }

  async function update(resource: Partial<Resource>) {
    try {
      const result = await $fetch<Resource | null>('/api/resource', {
        method: 'PUT',
        body: resource,
      })

      if (result) {
        const index = resources.value?.findIndex(r => r.id === result.id)
        if (index !== undefined && index > -1) {
          resources.value?.splice(index, 1, result)
        }
      }
      return { data: result, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }

  async function remove(id: number) {
    try {
      const result = await $fetch<Resource | null>(`/api/resource`, {
        method: 'DELETE',
        query: { id },
      })

      if (result) {
        const index = resources.value?.findIndex(r => r.id === id)
        if (index !== -1) {
          resources.value?.splice(index, 1)
        }
      }
      return { data: result, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }

  return { fetch, upsert, update, remove, normalizeData, resources }
})
